import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, Eye, ChevronRight, Search, BookOpen, Heart, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ThemeContext, API_URL } from './App';

function BlogListPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { themeStyles } = React.useContext(ThemeContext);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, [pagination.page, selectedCategory]);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/blog/posts?page=${pagination.page}&limit=9`;
            if (selectedCategory) {
                url += `&category=${selectedCategory}`;
            }
            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }
            
            const response = await axios.get(url);
            setPosts(response.data.posts || []);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Failed to fetch posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_URL}/blog/categories`);
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination({ ...pagination, page: 1 });
        fetchPosts();
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <HelmetProvider>
            <div className={`min-h-screen ${themeStyles.background}`}>
                <Helmet>
                    <title>Bible AI Blog | Christian Articles & Bible Study</title>
                    <meta name="description" content="Explore in-depth Bible study articles, Christian living advice, theological insights, and devotional content to help you grow in your faith." />
                    <meta name="keywords" content="Bible study, Christian blog, Scripture, faith, theology, devotionals, Christian living, Bible AI" />
                    <meta property="og:title" content="Bible AI Blog | Christian Articles & Bible Study" />
                    <meta property="og:description" content="Deepen your faith with insightful articles about Scripture, Christian living, and theology" />
                    <meta property="og:url" content="https://bibleai.top/blog" />
                    <meta property="og:type" content="website" />
                    <meta property="og:image" content="https://bibleai.top/og-image.jpg" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Bible AI Blog" />
                    <meta name="twitter:description" content="Christian articles, Bible study, and faith resources" />
                    <link rel="canonical" href="https://bibleai.top/blog" />
                </Helmet>

                {/* Hero Section */}
                <div className={`relative overflow-hidden ${themeStyles.accentBg} border-b ${themeStyles.border}`}>
                    <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            <span className={`bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent`}>
                                Bible AI Blog
                            </span>
                        </h1>
                        <p className={`text-xl ${themeStyles.secondaryText} max-w-2xl mx-auto`}>
                            Deepen your faith with insightful articles about Scripture, Christian living, and theology
                        </p>
                        
                        {/* Search Bar */}
                        <form onSubmit={handleSearch} className="max-w-md mx-auto mt-8">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`w-full px-4 py-3 pl-12 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${themeStyles.text} shadow-sm`}
                                />
                                <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeStyles.secondaryText}`} />
                                <button
                                    type="submit"
                                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 ${themeStyles.button} text-white rounded-lg text-sm`}
                                >
                                    Search
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 py-12">
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Sidebar */}
                        <aside className="lg:w-80 order-2 lg:order-1">
                            {/* Categories */}
                            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 mb-6 shadow-md`}>
                                <h3 className="font-semibold mb-4 flex items-center">
                                    <BookOpen className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
                                    Categories
                                </h3>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => {
                                            setSelectedCategory(null);
                                            setPagination({ ...pagination, page: 1 });
                                        }}
                                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                            !selectedCategory 
                                                ? `${themeStyles.accentBg} ${themeStyles.accent} font-medium`
                                                : `${themeStyles.hover} ${themeStyles.secondaryText}`
                                        }`}
                                    >
                                        All Posts
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => {
                                                setSelectedCategory(cat.slug);
                                                setPagination({ ...pagination, page: 1 });
                                            }}
                                            className={`w-full flex justify-between items-center px-3 py-2 rounded-lg transition-colors ${
                                                selectedCategory === cat.slug
                                                    ? `${themeStyles.accentBg} ${themeStyles.accent} font-medium`
                                                    : `${themeStyles.hover} ${themeStyles.secondaryText}`
                                            }`}
                                        >
                                            <span>{cat.name}</span>
                                            <span className="text-xs">{cat.post_count}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <div className="flex-1 order-1 lg:order-2">
                            {loading ? (
                                <div className="flex justify-center py-12">
                                    <div className={`w-8 h-8 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
                                </div>
                            ) : posts.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className={themeStyles.secondaryText}>No posts found.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {posts.map((post) => (
                                            <Link
                                                key={post.id}
                                                to={`/blog/${post.slug}`}
                                                className={`group ${themeStyles.card} rounded-xl border ${themeStyles.border} overflow-hidden hover:shadow-xl transition-all duration-300`}
                                            >
                                                {post.featured_image && (
                                                    <div className="h-48 overflow-hidden">
                                                        <img 
                                                            src={post.featured_image} 
                                                            alt={post.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                    </div>
                                                )}
                                                <div className="p-5">
                                                    <div className="flex items-center gap-3 text-xs mb-3">
                                                        <span className={`flex items-center ${themeStyles.secondaryText}`}>
                                                            <Calendar className="w-3 h-3 mr-1" />
                                                            {formatDate(post.published_at)}
                                                        </span>
                                                        <span className={`flex items-center ${themeStyles.secondaryText}`}>
                                                            <Eye className="w-3 h-3 mr-1" />
                                                            {post.views || 0}
                                                        </span>
                                                    </div>
                                                    <h3 className="text-xl font-bold mb-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                                                        {post.title}
                                                    </h3>
                                                    <p className={`${themeStyles.secondaryText} text-sm mb-4 line-clamp-3`}>
                                                        {post.excerpt}
                                                    </p>
                                                    <div className="flex items-center justify-between">
                                                        <span className={`text-sm ${themeStyles.accent} font-medium flex items-center`}>
                                                            Read More
                                                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    {pagination.pages > 1 && (
                                        <div className="flex justify-center gap-2 mt-8">
                                            <button
                                                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                                disabled={pagination.page === 1}
                                                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                                                    themeStyles.secondaryBg
                                                } border ${themeStyles.border} ${themeStyles.hover}`}
                                            >
                                                Previous
                                            </button>
                                            <span className={`px-4 py-2 ${themeStyles.accentBg} rounded-lg`}>
                                                Page {pagination.page} of {pagination.pages}
                                            </span>
                                            <button
                                                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                                disabled={pagination.page === pagination.pages}
                                                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                                                    themeStyles.secondaryBg
                                                } border ${themeStyles.border} ${themeStyles.hover}`}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </HelmetProvider>
    );
}

function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { themeStyles } = React.useContext(ThemeContext);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPost();
        window.scrollTo(0, 0);
    }, [slug]);

    const fetchPost = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/blog/posts/${slug}`);
            setPost(response.data.post);
            setRelatedPosts(response.data.related_posts || []);
        } catch (error) {
            console.error('Failed to fetch post:', error);
            if (error.response?.status === 404) {
                navigate('/blog');
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const shareOnFacebook = () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const shareOnTwitter = () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post?.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const shareOnLinkedIn = () => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${themeStyles.background}`}>
                <div className={`w-8 h-8 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
            </div>
        );
    }

    if (!post) return null;

    // Generate JSON-LD structured data
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt || post.meta_description,
        "author": {
            "@type": "Person",
            "name": post.author
        },
        "datePublished": post.published_at,
        "dateModified": post.updated_at || post.published_at,
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://bibleai.top/blog/${post.slug}`
        },
        "publisher": {
            "@type": "Organization",
            "name": "Bible AI",
            "logo": {
                "@type": "ImageObject",
                "url": "https://bibleai.top/logo.png"
            }
        },
        "image": post.featured_image || "https://bibleai.top/og-image.jpg",
        "keywords": post.categories?.join(", ") || "Bible study, Christian blog",
        "wordCount": post.content?.split(/\s+/).length || 0
    };

    return (
        <HelmetProvider>
            <div className={`min-h-screen ${themeStyles.background}`}>
                <Helmet>
                    <title>{post.title} | Bible AI Blog</title>
                    <meta name="description" content={post.meta_description || post.excerpt} />
                    <meta name="keywords" content={`Bible study, ${post.title}, Christian blog, Scripture, faith, ${post.categories?.join(", ")}`} />
                    
                    {/* Open Graph tags */}
                    <meta property="og:title" content={post.title} />
                    <meta property="og:description" content={post.excerpt} />
                    <meta property="og:url" content={`https://bibleai.top/blog/${post.slug}`} />
                    <meta property="og:type" content="article" />
                    <meta property="og:image" content={post.featured_image || "https://bibleai.top/og-image.jpg"} />
                    <meta property="og:site_name" content="Bible AI" />
                    <meta property="og:locale" content="en_US" />
                    
                    {/* Twitter Card */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={post.title} />
                    <meta name="twitter:description" content={post.excerpt} />
                    <meta name="twitter:image" content={post.featured_image || "https://bibleai.top/og-image.jpg"} />
                    
                    {/* Article specific */}
                    <meta property="article:published_time" content={post.published_at} />
                    <meta property="article:modified_time" content={post.updated_at || post.published_at} />
                    <meta property="article:author" content={post.author} />
                    <meta property="article:section" content={post.categories?.[0] || "Bible Study"} />
                    {post.categories?.map((cat, idx) => (
                        <meta key={idx} property="article:tag" content={cat} />
                    ))}
                    
                    {/* Canonical URL */}
                    <link rel="canonical" href={`https://bibleai.top/blog/${post.slug}`} />
                    
                    {/* JSON-LD Structured Data */}
                    <script type="application/ld+json">
                        {JSON.stringify(jsonLd)}
                    </script>
                </Helmet>

                <article className="max-w-4xl mx-auto px-4 py-12">
                    {/* Header */}
                    <header className="mb-8 text-center">
                        <div className="flex flex-wrap justify-center gap-2 mb-4">
                            {post.categories?.map((cat, idx) => (
                                <Link
                                    key={idx}
                                    to={`/blog?category=${post.category_slugs?.[idx]}`}
                                    className={`px-3 py-1 ${themeStyles.accentBg} ${themeStyles.accent} rounded-full text-sm`}
                                >
                                    {cat}
                                </Link>
                            ))}
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                            {post.title}
                        </h1>
                        <div className={`flex items-center justify-center gap-4 text-sm ${themeStyles.secondaryText}`}>
                            <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center">
                                <User className="w-4 h-4 mr-1" />
                                {post.author}
                            </span>
                            <span className="flex items-center">
                                <Eye className="w-4 h-4 mr-1" />
                                {post.views} views
                            </span>
                        </div>
                    </header>

                    {/* Featured Image */}
                    {post.featured_image && (
                        <div className="mb-8 rounded-xl overflow-hidden shadow-xl">
                            <img 
                                src={post.featured_image} 
                                alt={post.title}
                                className="w-full h-auto"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div className={`prose prose-lg max-w-none ${themeStyles.prose}`}>
                        <ReactMarkdown>
                            {post.content}
                        </ReactMarkdown>
                    </div>

                    {/* Share Section */}
                    <div className={`mt-8 pt-8 border-t ${themeStyles.border}`}>
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                            <Share2 className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
                            Share this article
                        </h3>
                        <div className="flex gap-3">
                            <button
                                onClick={shareOnFacebook}
                                className="p-2 bg-[#1877f2] text-white rounded-lg hover:opacity-90 transition-opacity"
                                aria-label="Share on Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </button>
                            <button
                                onClick={shareOnTwitter}
                                className="p-2 bg-[#1da1f2] text-white rounded-lg hover:opacity-90 transition-opacity"
                                aria-label="Share on Twitter"
                            >
                                <Twitter className="w-5 h-5" />
                            </button>
                            <button
                                onClick={shareOnLinkedIn}
                                className="p-2 bg-[#0077b5] text-white rounded-lg hover:opacity-90 transition-opacity"
                                aria-label="Share on LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Related Posts */}
                    {relatedPosts.length > 0 && (
                        <div className="mt-12 pt-8 border-t ${themeStyles.border}">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <Heart className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
                                You might also like
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {relatedPosts.map((related) => (
                                    <Link
                                        key={related.id}
                                        to={`/blog/${related.slug}`}
                                        className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-4 hover:shadow-lg transition-all`}
                                    >
                                        <h4 className="font-semibold mb-2 line-clamp-2">{related.title}</h4>
                                        <p className={`text-sm ${themeStyles.secondaryText} line-clamp-2`}>
                                            {related.excerpt}
                                        </p>
                                        <span className={`text-xs ${themeStyles.accent} mt-2 inline-block`}>
                                            Read More →
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </article>
            </div>
        </HelmetProvider>
    );
}

export { BlogListPage, BlogPostPage };